import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Button,
} from '@mui/material';
import { Info } from '@mui/icons-material';
import { useTranslation } from '@/core/i18n/i18n';

interface OutOfTimeDecisionDialogProps {
  open: boolean;
  onClose: () => void;
  isSubmitting: boolean;
  onDecision: (decision: 'accept' | 'reject') => void;
  rejectionReason: string;
  setRejectionReason: (reason: string) => void;
}

const OutOfTimeDecisionDialog: React.FC<OutOfTimeDecisionDialogProps> = ({
  open,
  onClose,
  isSubmitting,
  onDecision,
  rejectionReason,
  setRejectionReason,
}) => {
  const { t } = useTranslation();
  const trimmedReason = rejectionReason.trim();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 1,
          boxShadow: 4,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
          {t('annualPlanning.outOfTimeDecisionTitle')}
        </Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ px: 3, py: 2 }}>
        <Box
          sx={{
            bgcolor: '#e8f5f5',
            borderRadius: 1,
            p: 2.5,
            border: '1px solid #00BCE4',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 2,
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#00BCE4',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Info sx={{ color: 'white' }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ color: '#004d52', lineHeight: 1.5 }}>
              {t('annualPlanning.outOfTimeDecisionMessage')}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#333' }}>
            {t('annualPlanning.outOfTimeDecisionReasonLabel')}{' '}
            <span style={{ color: '#d32f2f' }}>*</span>
          </Typography>
          <textarea
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            rows={4}
            placeholder={t('annualPlanning.outOfTimeDecisionReasonPlaceholder')}
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: 6,
              border: '1px solid #c4cdd5',
              fontFamily: 'inherit',
              fontSize: '0.9rem',
              resize: 'vertical',
              outline: 'none',
              boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)',
            }}
          />
          {!trimmedReason && (
            <Typography variant="caption" sx={{ color: '#d32f2f' }}>
              {t('annualPlanning.outOfTimeDecisionReasonRequired')}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
        <Button
          onClick={() => onDecision('reject')}
          disabled={isSubmitting || !trimmedReason}
          color="error"
          variant="outlined"
          sx={{
            px: 3,
            borderRadius: 20,
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          {t('common.reject')}
        </Button>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            onClick={onClose}
            disabled={isSubmitting}
            variant="outlined"
            sx={{
              px: 3,
              borderRadius: 20,
              textTransform: 'none',
              fontWeight: 600,
              borderColor: '#00BCE4',
              color: '#00BCE4',
              '&:hover': { borderColor: '#00acc1', bgcolor: 'rgba(0,188,212,0.04)' },
            }}
          >
            {t('common.close')}
          </Button>
          <Button
            onClick={() => onDecision('accept')}
            color="primary"
            variant="contained"
            disabled={isSubmitting}
            sx={{
              px: 3,
              borderRadius: 20,
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: '#00BCE4',
              '&:hover': { bgcolor: '#00acc1' },
            }}
          >
            {t('common.approve')}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default React.memo(OutOfTimeDecisionDialog);
